import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Dispatch } from "redux";
import { connect, useStore } from "react-redux";
import moment from "moment-with-locales-es6";

import { postUserPreferences } from "services/requests/v1/user";

import { InitialPreferencesType } from "redux/reducers/preferences/types";

import RegionParameterContent from "./region-parameter-content/region-parameter-content.component";
import TimeFormatContent from "./time-format-content/time-format-content.component";
import RestoreButton from "components/generic/restore-button.component";
import ModalLayout from "components/shared/modal-layout.component";
import GoAiguaButton from "../goaiguabutton";

import { setUserPreferences } from "redux/reducers/preferences/actions";

import { updateLanguage } from "redux/reducers/global/actions";
import { getEnvVariable } from "../../../utils/environment";
import {
    SetRequestError,
    SubscribeContentType,
    UnsubscribeContentType,
} from "types/generic";

type PreferencesModalContentType = {
    preferences: InitialPreferencesType;
    updateModalVisibility: () => void;
    updatePreferences: (
        language: string,
        dateFormat: string,
        timeFormat: string,
        weekStartDay: string
    ) => void;
    subscribeContent: SubscribeContentType;
    unsubscribeContent: UnsubscribeContentType;
    setError: SetRequestError;
    contentName: string;
};

const PreferencesModalContent = ({
    preferences,
    contentName,
    updateModalVisibility,
    updatePreferences,
    setError,
    subscribeContent,
    unsubscribeContent,
}: PreferencesModalContentType) => {
    const intl = useIntl();
    const store = useStore();

    const unitsIsVisible = getEnvVariable("REACT_APP_UNITS_VISIBLE");
    const [region, setRegion] = useState<{
        language: string;
    }>({ language: "" });

    const [timeDateFormat, setTimeDateFormat] = useState<{
        dateFormat: string;
        timeFormat: string;
        weekStartDay: string;
    }>({
        dateFormat: "",
        timeFormat: "",
        weekStartDay: "",
    });
    const [unitsFormat, setUnitsFormat] = useState<{
        caudalFormat: string;
        volumeFormat: string;
        preasureFormat: string;
        lengthFormat: string;
        areaFormat: string;
    }>({
        caudalFormat: "",
        volumeFormat: "",
        preasureFormat: "",
        lengthFormat: "",
        areaFormat: "",
    });

    const [isBtnActive, setIsBtnActive] = useState<boolean>(false);

    const [exampleModalPreferences, setExampleModalPreferences] = useState<
        string[]
    >([]);

    const [exampleUnityModalPreferences, setExampleUnityModalPreferences] =
        useState<string[]>([]);

    const isEquivalent = (a: any, b: any) => {
        // Create arrays of property names
        var aProps = Object.getOwnPropertyNames(a);
        var bProps = Object.getOwnPropertyNames(b);

        // If number of properties is different,
        // objects are not equivalent
        if (aProps.length != bProps.length) {
            return false;
        }

        for (var i = 0; i < aProps.length; i++) {
            var propName = aProps[i];

            // If values of same property are not equal,
            // objects are not equivalent
            if (a[propName] !== b[propName]) {
                return false;
            }
        }

        // If we made it this far, objects
        // are considered equivalent
        return true;
    };

    const restoreCallback = () => {
        setRegion(preferences.region);
        setTimeDateFormat(preferences.timeDateFormat);
        setIsBtnActive(false);
    };

    const saveCallback = async () => {
        subscribeContent(contentName);

        try {
            updateModalVisibility();

            await postUserPreferences(
                timeDateFormat.dateFormat,
                region.language,
                timeDateFormat.timeFormat,
                timeDateFormat.weekStartDay
            );

            updatePreferences(
                region.language,
                timeDateFormat.dateFormat,
                timeDateFormat.timeFormat,
                timeDateFormat.weekStartDay
            );

            unsubscribeContent(contentName, true);
        } catch (error) {
            setError(contentName);
        }

        store.dispatch(updateLanguage(region.language));
    };

    const tabs: any[] = [
        {
            id: "general-preferences",
            title: intl.formatMessage({
                id: "app.components.preferences.general",
            }),
            content: () => (
                <>
                    <RegionParameterContent
                        region={region}
                        setRegion={setRegion}
                        setIsBtnActive={setIsBtnActive}
                    />
                    <TimeFormatContent
                        timeDateFormat={timeDateFormat}
                        setTimeDateFormat={setTimeDateFormat}
                        setIsBtnActive={setIsBtnActive}
                        exampleModalPreferences={exampleModalPreferences}
                    />
                    <RestoreButton
                        restoreCallback={restoreCallback}
                        isBtnActive={isBtnActive}
                    />
                </>
            ),
        },
    ];

    useEffect(() => {
        setRegion(preferences.region);
        setTimeDateFormat(preferences.timeDateFormat);
    }, [preferences]);

    useEffect(() => {
        if (preferences && timeDateFormat && region) {
            const generalPreferences = {
                ...timeDateFormat,
                ...region,
            };
            const savedPreferences = {
                ...preferences.timeDateFormat,
                ...preferences.region,
            };

            const areEquals = isEquivalent(
                generalPreferences,
                savedPreferences
            );
            setIsBtnActive(areEquals ? false : true);
        }
    }, [timeDateFormat, region]);

    useEffect(() => {
        let locale = region.language;
        let dateFormat = timeDateFormat.dateFormat;
        let timeFormat = timeDateFormat.timeFormat;

        const exampleDataLong = moment().locale(locale).format("LLLL");
        const exampleDataShort = moment()
            .locale(locale)
            .format(`${dateFormat} ${timeFormat}`);

        setExampleModalPreferences([exampleDataLong, exampleDataShort]);
    }, [region, timeDateFormat]);

    return (
        <>
            <main>
                <ModalLayout tabs={tabs} showTabs={true} />
            </main>

            <footer className="c-modal__footer">
                <GoAiguaButton
                    text={intl.formatMessage({
                        id: "app.components.preferences.cancel",
                    })}
                    className={"c-modal__footerbtn c-modal__footerbtn--transp"}
                    onClick={() => updateModalVisibility()}
                />
                <GoAiguaButton
                    text={intl.formatMessage({
                        id: "app.components.preferences.save",
                    })}
                    className={"c-modal__footerbtn  "}
                    onClick={() => {
                        saveCallback();
                    }}
                    status={isBtnActive ? "enabled" : "disabled"}
                />
            </footer>
        </>
    );
};

const mapStateToProps = (state: any) => {
    return {
        preferences: state.preferences,
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
    return {
        updatePreferences: (
            language: string,
            dateFormat: string,
            timeFormat: string,
            weekStartDay: string
        ) =>
            dispatch(
                setUserPreferences(
                    language,
                    dateFormat,
                    timeFormat,
                    weekStartDay
                )
            ),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(PreferencesModalContent);
